import React, { useEffect, useContext } from 'react';
import { EiqSelect } from 'eiq-storybook';
import { QuestionDependentDropdownDefaultProps, QuestionDependentDropdownPropTypes } from 'src/components/JobDatasheet/propTypes';
import { OptionWithDefinition } from 'src/components/SelectBox';
import { useTranslation } from 'react-i18next';
import JobDatasheetLanguageContext from 'src/components/JobDatasheet/context/JobDatasheetLanguageContext';
import s from 'src/components/JobDatasheet/JobDatasheetForm/JobDatasheetForm.styles';

export const QuestionDependentDropdown = ({
  allowSearch,
  errors,
  flash,
  isRectifiedQuestion,
  onChange,
  parentOfActiveQuestion,
  question: {
    dependentDropdownSettings: { dependsOn, allowMultiSelect, options },
    translations,
  },
  value,
  getResult,
  allQuestions,
  skipPlaceholder = false,
  inTable = false,
}) => {
  const { t } = useTranslation();

  const { selectedLanguage } = useContext(JobDatasheetLanguageContext);

  const getOptionLabel = (label, index) => {
    if (!selectedLanguage) {
      return label;
    }

    const labelTranslation = (translations || []).find(c => c.languageId === selectedLanguage)?.options?.[index];

    /**
     * if translation has changed payload to accommodate definition then
     * return labelTranslation?.value if not then
     * return labelTranslation, if this is also not present then
     * return option's value property by default
     */
    return labelTranslation?.label ?
      labelTranslation.label :
      labelTranslation || label;
  };

  const getOptionDefinition = (option, index) => {
    if (!selectedLanguage) {
      return option.definition;
    }

    const definitionTranslation = (translations || []).find(c => c.languageId === selectedLanguage)?.options?.[index];

    /**
     * if translation supports definition then
     * return labelTranslation?.definition if not then
     * that means translation's options only contains data for value and not definition
     * therefore return option's definition property by default
     */
    return definitionTranslation?.definition ?
      definitionTranslation.definition :
      option.definition;
  };

  const getParentResult = () => {
    if (inTable) {
      return (getResult ? getResult(dependsOn) : null);
    }
    return (getResult ? getResult({ datasheetQuestionId: dependsOn })?.result : null);
  }

  const parentResult = getParentResult();
  let parentValue = null;
  const rectifiedParentResult = parentResult?.rectifiedValue || null;
  const actualParentResult = parentResult?.value || null;
  if (isRectifiedQuestion) {
    parentValue = rectifiedParentResult;
    if (!rectifiedParentResult || (Array.isArray(rectifiedParentResult) && !rectifiedParentResult.length)) {
      parentValue = actualParentResult;
    }
  } else {
    parentValue = actualParentResult;
  }

  const parentQuestion = parentOfActiveQuestion || (allQuestions || []).find(c => c.datasheetQuestionId === dependsOn);
  const isParentMultiSelect =
    parentQuestion?.datasheetQuestion?.currentVersion?.dropdownSettings?.allowMultiSelect ||
    parentQuestion?.datasheetQuestion?.currentVersion?.dependentDropdownSettings?.allowMultiSelect ||
    parentQuestion?.dropdownSettings?.allowMultiSelect ||
    parentQuestion?.dependentDropdownSettings?.allowMultiSelect ||
    parentQuestion?.allowMultiSelect;
    
  let parentQuestionText = parentQuestion?.text || parentQuestion?.datasheetQuestion?.currentVersion?.text || '';
  if (parentQuestionText.length > 20) {
    parentQuestionText = `${parentQuestionText.substr(0, 20)}...`;
  }

  const selectOptions = options?.map((option, index) => ({
    ...option,
    translatedLabel: getOptionLabel(option.label, index),
    definition: getOptionDefinition(option, index)
  })).filter(({ parent }) => {
    if (isParentMultiSelect) {
      return (parentValue || []).includes(parent);
    }
    return parent === parentValue;
  }).map(({ label, translatedLabel, definition }) => ({
    value: label,
    label: translatedLabel,
    subText: definition,
  }));

  const selectedValueKey = isRectifiedQuestion ? 'rectifiedValue' : 'value';
  const realValue = (value || { value: null })[selectedValueKey] || null;

  const selectedValue = allowMultiSelect
    ? selectOptions?.filter(v => (realValue || []).indexOf(v.value) !== -1)
    : selectOptions?.find(v => v.value === realValue) || null;
    

  const getReturnObject = (e, withMultiSelect) => {
    const returnObject = {
      ...value,
      type: 'dependentDropdown'
    };

    let returnValue;

    if (withMultiSelect) {
      returnValue = e ? e.map(v => v.value) : [];
    } else {
      returnValue = e ? e.value : null;
    }

    returnObject[selectedValueKey] = returnValue;

    return returnObject;
  };

  useEffect(() => {
    if(parentValue){
      if (!realValue || (realValue.length === 0)) {
        return;
      }
      if (!selectedValue || (selectedValue.length === 0)) {
        onChange({...value, type: 'dependentDropdown', value: allowMultiSelect ? [] : null });
      }
    }
  }, [parentValue]);

  const displaySelectedOptionsDefinition = () => {
    if (selectedValue) {
      if (allowMultiSelect) {
        return (
          <s.OptionDefinitionContainer>
            {selectedValue
              .filter(v => !!v.subText)
              .map((v, i) => <s.QuestionLabel key={i}>{v.value}: {v.subText}</s.QuestionLabel>)
            }
          </s.OptionDefinitionContainer>
        );
      }

      if (selectedValue.subText) {
        return (
          <s.OptionDefinitionContainer>
            <s.QuestionLabel>{selectedValue.value}: {selectedValue.subText}</s.QuestionLabel>
          </s.OptionDefinitionContainer>
        );
      }
    }
    return null;
  };

  const placeholder = (isParentMultiSelect ? parentValue?.length : parentValue)
            ? t('SELECT_PLACEHOLDER')
            : t('DATASHEET_DEPENDENT_NO_PARENT', { question: parentQuestionText });

  return (
    <>
      <s.QuestionDropdownWrapper>
        <EiqSelect 
          isMulti = {allowMultiSelect} 
          isSearchEnable = {allowSearch}
          isClearable={true}
          disabled={isParentMultiSelect ? !parentValue?.length : !parentValue}
          placeholder={!skipPlaceholder && placeholder}
          onChange={ (_name, option) => {
            onChange(getReturnObject(option, allowMultiSelect));
          }}        
          options={selectOptions}
          optionRenderer={OptionWithDefinition}
          value={selectedValue}
          />
        {displaySelectedOptionsDefinition()}
      </s.QuestionDropdownWrapper>
      {displaySelectedOptionsDefinition()}
    </>
  );
};

QuestionDependentDropdown.propTypes = QuestionDependentDropdownPropTypes;
QuestionDependentDropdown.defaultProps = QuestionDependentDropdownDefaultProps;
